import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable password-confirm modal for destructive actions.
 * Props:
 *  - open (bool)
 *  - onOpenChange (fn)
 *  - title, description, itemLabel (strings)
 *  - getAuthHeaders (fn returning axios config with Authorization)
 *  - onConfirmed (async fn) — runs the destructive action after password is verified
 */
const PasswordConfirmModal = ({ open, onOpenChange, title = 'Confirm Delete', description, itemLabel, getAuthHeaders, onConfirmed }) => {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
      setSubmitting(false);
      // autofocus after dialog mounts
      setTimeout(() => { try { inputRef.current?.focus(); } catch (e) { /* noop */ } }, 80);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!password) { setError('Password required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/auth/verify-password`, { password }, getAuthHeaders());
      try {
        await onConfirmed();
        onOpenChange(false);
      } catch (actionErr) {
        // The destructive action itself failed (e.g., dependency block) — surface the server message
        const msg = actionErr?.response?.data?.detail || 'Action failed';
        setError(msg);
      }
    } catch (verifyErr) {
      const msg = verifyErr?.response?.status === 401 ? 'Incorrect password' : 'Verification failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="password-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <ShieldAlert className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-1">
          <div className="text-sm text-[#475569] space-y-2">
            {description && <p>{description}</p>}
            {itemLabel && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-xs">
                <span className="font-medium text-red-700">Target: </span>
                <span className="text-red-800 font-mono break-all" data-testid="pwd-confirm-item-label">{itemLabel}</span>
              </div>
            )}
            <p className="text-xs text-[#64748b]">Re-enter your password to confirm this destructive action.</p>
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-xs font-medium">Your password</Label>
            <Input
              id="confirm-password"
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
              autoComplete="current-password"
              className="mt-1 h-10"
              data-testid="pwd-confirm-input"
              disabled={submitting}
            />
            {error && <p className="text-xs text-red-600 mt-1.5" data-testid="pwd-confirm-error">{error}</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="pwd-confirm-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting || !password} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pwd-confirm-submit">
              {submitting ? <span className="flex items-center gap-1.5"><span className="animate-spin h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full"></span>Verifying…</span> : <span className="flex items-center gap-1.5"><Trash2 className="h-3.5 w-3.5" />Delete</span>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordConfirmModal;
